import { AxiosError, AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import userClient from './userClient';
import client from 'api/client';
import { systemId } from 'src/app.config';
import {
  LoginResponse,
  IUser,
  IdentityResponse,
  Token,
} from 'types/loginResponse';
import { Claims } from 'src/types/claims';

export async function Me(): Promise<IUser | undefined> {
  try {
    const user = await client.get<IUser>('/Login/Me');
    if (user.status) return user.data;
    return undefined;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.data) return err.response.data as IUser;
    return undefined;
  }
}
export interface LoginProps {
  username: string;
  password: string;
}

export async function Login(loginProps: LoginProps) {
  try {
    const response = await requestIdentityCookie(loginProps);
    if (response.status !== 200)
      return 'Ha ocurrido un error en la identificación.';
    const { identity } = response.data;
    localStorage.setItem('IdentityData', JSON.stringify(identity));
    const version = localStorage.getItem('version') ?? '';
    const { data: auRes } = await requestAuthorization(version);
    if (auRes.token == '') return 'Ha ocurrido un error en la autentificación.';

    const { claimsResponse, succeeded } = getAutorizationResponse(auRes.token);
    const { userName, name, email, rut, rol } = identity;

    return {
      authorization: auRes.token,
      user: {
        userName,
        name,
        email,
        claims: claimsResponse,
        rut,
        rol: Number(rol),
      } as unknown as IUser,
      succeeded,
    } as LoginResponse;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.data) return err.response.data as string;
    return 'Ha ocurrido un error en la autentificación.';
  }
}

export const getAutorizationResponse = (authorization: string) => {
  const claimsResponse: Claims = jwtDecode(authorization);
  let succeeded = true;
  if (new Date(claimsResponse.exp * 1000) < new Date()) {
    succeeded = false;
  }
  return {
    claimsResponse,
    succeeded,
  };
};

export const requestIdentityCookie = (loginProps: LoginProps) => {
  return userClient.post<IdentityResponse>(
    '/api/Token/RequestIdentityCookie',
    loginProps,
    {
      withCredentials: true,
    }
  );
};

export const refreshToken = (): Promise<AxiosResponse<Token>> => {
  return userClient.get<Token>('api/Token/RefreshToken', {
    withCredentials: true,
  });
};

export const requestAuthorization = (
  version: string
): Promise<AxiosResponse<Token>> => {
  return userClient.get<Token>('/api/Token/RequestAuthorization', {
    params: {
      systemId,
      version,
    },
    withCredentials: true,
  });
};

export interface ChangePasswordProps {
  userName: string;
  currentPassword: string;
  newPassword: string;
}

export interface ChangeEmailProps {
  UserName: string;
  NewEmail: string;
  Rut: string;
}

export async function ChangePassword(
  changeProps: ChangePasswordProps
): Promise<{ ok: boolean; message: string }> {
  try {
    const response = await userClient.put<string>(
      '/api/User/ChangePassword',
      changeProps
    );
    switch (response.status) {
      case 200:
        return {
          ok: true,
          message: 'Se ha modificado correctamente la contraseña.',
        };
      case 400:
        return { ok: false, message: 'Usuario incorrecto.' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.status === 400)
      return { ok: false, message: 'Usuario incorrecta.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar la contraseña.',
    };
  }
}
//Esta funcion tiene que hacer el cambio del correo en user managaer y en driverdb en ¿Gestion?
export async function ChangeEmail(
  changeProps: ChangeEmailProps
): Promise<{ ok: boolean; message: string; profileName?: string }> {
  try {
    const responseUser = await userClient.put<{
      message: string;
      profileName: string;
    }>('/api/User/ChangeEmail', changeProps);

    switch (responseUser.status) {
      case 200:
        return {
          ok: true,
          message: responseUser.data.message,
          profileName: responseUser.data.profileName,
        };
      case 400:
        return { ok: false, message: 'Usuario incorrecto.' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.status === 400)
      return { ok: false, message: 'Usuario incorrecto.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar el correo.',
    };
  }
}

export const resetPassword = (userName: string): Promise<AxiosResponse<void>> =>
  userClient.put(
    '/api/User/ResetPasswordUser',
    {},
    {
      params: {
        userName,
      },
    }
  );

export interface LogoutProps {
  authorization: string;
  version: string;
}

export const Logout = ({ authorization, version }: LogoutProps) => {
  return userClient.get('/api/ConnectionLog/RegisterLogout', {
    headers: {
      Authorization: `Bearer ${authorization}`,
    },
    params: {
      systemId,
      version,
    },
    withCredentials: true,
  });
};
